import { InsertDriveFile as FileIcon } from "@mui/icons-material";
import type { UploadedFile, JobResponse } from "@taxassist/api-client";
import { canDeleteFile } from "../../utils/fileUtils";
import {
  FileList,
  type FileItem,
  type FileListCallbacks,
  type FileListConfig,
  type UploadedFileType,
  type BucketType,
  BUCKET_SUBHEADER_LABELS,
} from "./fileList";
import { Tooltip, Box } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { groupBy } from "lodash";

interface UploadedFilesListProps {
  files: FileItem[];
  job?: JobResponse; // Job context for delete validation
  onDownload?: (file: FileItem) => void;
  onPreview?: (file: FileItem, index: number) => void;
  onDelete?: (file: FileItem) => Promise<void>; // Callback after successful deletion
  onUpdateFileType?: (
    file: FileItem,
    newType: UploadedFileType,
  ) => Promise<void>; // Callback for updating file type
  onUpdateBucket?: (file: FileItem, newBucket: BucketType) => Promise<void>; // Callback for updating bucket
  compact?: boolean;
  showActions?: boolean;
  maxItems?: number;
  allowDelete?: boolean; // Whether to show delete buttons
  getFileUrl?: (file: FileItem) => Promise<string>; // Function to get presigned URLs for file preview
  enableViewer?: boolean; // Whether to enable the file viewer dialog
  fileSubmissionEvaluator?: (file: FileItem) => boolean; // Configurable function to evaluate file submission status
  groupIntoBuckets?: boolean;
}

// Custom delete validation function for uploaded files
const validateFileDelete = (
  file: FileItem,
  job?: JobResponse,
): { canDelete: boolean; reason?: string } => {
  if (!job) return { canDelete: false, reason: "No job context available" };

  // Convert back to UploadedFile for existing validation logic
  const uploadedFile: UploadedFile = {
    fileName: file.fileName,
    s3Key: file.s3Key,
    fileSize: file.fileSize,
    contentType: file.contentType,
    uploadedAt: file.uploadedAt,
  };

  return canDeleteFile(uploadedFile, job);
};

export function UploadedFilesList({
  files,
  job,
  onDownload,
  onPreview,
  onDelete,
  onUpdateFileType,
  onUpdateBucket,
  compact = false,
  showActions = true,
  maxItems,
  allowDelete = true,
  getFileUrl,
  enableViewer = true,
  fileSubmissionEvaluator,
  groupIntoBuckets = true,
}: UploadedFilesListProps) {
  const callbacks: FileListCallbacks = {
    onPreview: onPreview,
    onDownload: onDownload,
    onDelete: onDelete,
    getFileUrl: getFileUrl,
    onUpdateFileType: onUpdateFileType as any,
    onUpdateBucket: onUpdateBucket as any,
  };

  // Configuration for the FileList component
  const config: FileListConfig = {
    showActions,
    compact,
    maxItems,
    allowDelete,
    enableViewer,
    emptyMessage: "No files uploaded yet",
    emptyIcon: (
      <FileIcon sx={{ fontSize: compact ? 32 : 48, mb: 1, opacity: 0.5 }} />
    ),
    showTypeBadges: !!onUpdateFileType || !!onUpdateBucket,
  };

  if (groupIntoBuckets) {
    const normalizedFiles = files.map((file) => {
      const expectedBucket = file.expectedBucket || "unsupported";
      return file.expectedBucket !== expectedBucket
        ? { ...file, expectedBucket }
        : file;
    });

    const buckets = groupBy(normalizedFiles, "expectedBucket");

    // Define the desired order: Ready to submit, Complex K1s, Unsupported
    const bucketOrder: BucketType[] = ["sureprep", "k1_complex", "unsupported"];
    const sortedBucketKeys = bucketOrder.filter(key => buckets[key]);

    return (
      <>
        {sortedBucketKeys.map((key) => {
          const label = BUCKET_SUBHEADER_LABELS[key] ?? "Other";

          // Add tooltip for Complex K1s section
          const subheader = key === "k1_complex" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {label}
              <Tooltip title="Cannot go through standard submission" placement="top">
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              </Tooltip>
            </Box>
          ) : (
            label
          );

          return (
            <FileList
              key={key}
              subheader={subheader}
              files={buckets[key]}
              callbacks={callbacks}
              config={{ ...config, compact: true, sx: { mb: 1 } }}
              fileTypeCategory="uploaded"
              fileSubmissionEvaluator={fileSubmissionEvaluator}
              deleteContext={job}
              canDeleteFile={validateFileDelete}
            />
          );
        })}
      </>
    );
  }

  return (
    <FileList
      files={files}
      callbacks={callbacks}
      config={{ ...config, compact: true }}
      fileTypeCategory="uploaded"
      fileSubmissionEvaluator={fileSubmissionEvaluator}
      deleteContext={job}
      canDeleteFile={validateFileDelete}
    />
  );
}
